import { FC, useEffect, useMemo, useRef, useState } from "react";
import styles from "./index.module.scss";
import usePosterInfo from "../../hooks/use-poster-info";
import cn from "classnames";
import PythonTipIcon from "./icons/pythontip";
import HashIcon from "./icons/hash";
import { takeSnapshot } from "../../utils/punch";
import dayjs from "dayjs";
import useProfile from "../../hooks/use-profile";
import { useNavigate } from "react-router-dom";

const themes = ["blue", 'pink'];
// 随机主题
const themeCacheKey = 'punch-card-theme';
const defaultTheme = function () {
  const cacheTheme = localStorage.getItem(themeCacheKey);
  if (cacheTheme && themes.includes(cacheTheme)) {
    return cacheTheme;
  }
  const index = Math.floor(Math.random() * themes.length);
  const theme = themes[index];
  return theme;
}()

interface PunchCardProps {
  className?: string;
  params: {
    course_no: string;
    page_id: number;
  };
  onLoad?: () => void;
}

const PunchCard: FC<PunchCardProps> = ({ className, params, onLoad }) => {
  const poster = usePosterInfo(params);
  const profile = useProfile();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [curTheme, setCurTheme] = useState(defaultTheme);

  const season = useMemo(() => {
    if (!profile) return "";
    if (!profile.seasons || !profile.seasons.length) return "";
    // 返回最后一个
    return profile.seasons[profile.seasons.length - 1];
  }, [profile]);

  const images = useMemo(() => {
    if (!poster) return [];
    const infos = poster.poster_infos;
    if (!infos || !infos.length) return [];
    const files = infos.flatMap((info) =>
      info.snapshots.map((snapshot) => snapshot.file)
    );
    return files.slice(0, 4);
  }, [poster]);

  const courseTitle = useMemo(() => {
    if (!poster) return "";
    const course = poster.poster_infos?.[0]?.course;
    return course?.title || "";
  }, [poster]);

  const pageTitle = useMemo(() => {
    if (!poster) return "";
    const course = poster.poster_infos?.[0]?.course;
    const page = poster.poster_infos?.[0]?.page;
    return page?.title || course?.title || "";
  }, [poster]);

  const cardRef = useRef<HTMLDivElement>(null);

  const downloadAndPunch = async () => {
    if (loading) return;
    if (!cardRef.current) return;
    setLoading(true);
    // download
    const snapshot = await takeSnapshot(cardRef.current);
    if (!snapshot) return;
    let a = document.createElement("a");
    a.href = snapshot;
    a.download = `PythonTip打卡-${dayjs().format("YYYY-MM-DD")}.png`;
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    a.remove();
    // punch
    if (season) {
      navigate(`/season/${season.id}`);
    }
    setTimeout(() => {
      setLoading(false);
    }, 1000);
  };

  useEffect(() => {
    if (profile && poster) {
      onLoad?.();
    }
  }, [poster, profile, onLoad]);

  if (!poster) return null;
  if (!images.length) return null;

  return (
    <div className={cn(styles.punchCard, className)}>
      <div className={cn(styles.card, styles[curTheme])} ref={cardRef}>
        <div className={styles.title}>
          <div>{poster.learn_day} DAYS</div>
          <div className={styles.course}>{courseTitle}</div>
        </div>
        <div className={styles.branch}>
          <PythonTipIcon />
          <HashIcon />
        </div>
        <div className={styles.avatar}>
          <img src={poster.user.avatar} alt="" />
        </div>
        <div className={styles.content}>
          <div className={styles.learn}>
            <div>今日学习</div>
            <div># {pageTitle}</div>
          </div>
          <div className={styles.snapshot}>
            {images.map((image, index) => (
              <img src={image} alt="" key={index} crossOrigin="anonymous" />
            ))}
          </div>
        </div>
      </div>

      <div className={styles.actions}>
        <div className={styles.themes}>
          <div className={styles.themeList}>
            {themes.map(theme => (
              <div
                className={cn(styles.themeBtn, styles[theme], theme === curTheme && styles.active)}
                key={theme}
                onClick={() => {
                  localStorage.setItem(themeCacheKey, theme);
                  setCurTheme(theme)
                }}
              />
            ))}
          </div>
          <div className={styles.themeTitle}>切换风格</div>
        </div>
        <div className={cn(styles.button, styles[curTheme])} onClick={downloadAndPunch}>
          {!!season ? "保存图片去打卡" : "保存图片分享"}
        </div>
      </div>

      {!!season && (
        <div className={styles.tips}>
          恭喜！已完成今日学习任务，鼓励大家自行根据学习收获做笔记分享小红书，单篇笔记1周内收获点赞100+ 再额外奖励100元🧧哦   也可直接下载该图片，分享小红书，并带上话题 #pythontip  #哈希编程 #python学习（必填哦），#编程 #python （可选的）
        </div>
      )}
    </div>
  );
};

export default PunchCard;
